import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import {
  Text,
  Card,
  IconButton,
  useTheme,
  ActivityIndicator,
} from 'react-native-paper';
import { Sun, Moon, Coffee, Sparkles, RefreshCw } from 'lucide-react-native';
import type { DashboardInsights } from '@coffeeplexity/shared';

interface AIGreetingHeaderProps {
  greeting?: string | null;
  insights?: DashboardInsights | null;
  loading?: boolean;
  onRefresh?: () => void;
}

export function AIGreetingHeader({
  greeting,
  insights,
  loading = false,
  onRefresh,
}: AIGreetingHeaderProps) {
  const theme = useTheme();

  // Generate contextual greeting if none provided
  const contextualGreeting = useMemo(() => {
    if (greeting) return greeting;

    const hour = new Date().getHours();
    const timeOfDay =
      hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
    const timeIcon = hour < 12 ? Sun : hour < 17 ? Sun : Moon;

    const greetings = {
      morning: [
        'Good morning! Ready to brew something amazing?',
        "Morning! Let's start your coffee journey today.",
        'Rise and grind! Your perfect cup awaits.',
      ],
      afternoon: [
        'Good afternoon! Time for your next coffee adventure?',
        'Afternoon pick-me-up time! What shall we explore?',
        'Perfect timing for some coffee learning!',
      ],
      evening: [
        'Good evening! Wind down with some coffee knowledge.',
        "Evening! Let's explore something new together.",
        "Perfect time to plan tomorrow's brew!",
      ],
    };

    const randomGreeting =
      greetings[timeOfDay][
        Math.floor(Math.random() * greetings[timeOfDay].length)
      ];

    return randomGreeting;
  }, [greeting]);

  // Get contextual icon based on time and insights
  const getContextualIcon = () => {
    const hour = new Date().getHours();
    if (hour < 12) return Sun;
    if (hour < 17) return Coffee;
    return Moon;
  };

  const ContextualIcon = getContextualIcon();

  // Generate AI insight message
  const aiInsightMessage = useMemo(() => {
    if (!insights) return null;

    const { streakInfo, upcomingMilestones, progressInsights } = insights;

    if (streakInfo?.current > 0) {
      return `🔥 ${streakInfo.current} day streak! ${streakInfo.encouragement}`;
    }

    if (upcomingMilestones?.length > 0) {
      const nextMilestone = upcomingMilestones[0];
      const progressPercent = Math.round(
        (nextMilestone.progress / nextMilestone.target) * 100
      );
      return `🎯 ${progressPercent}% to your next milestone: ${nextMilestone.title}`;
    }

    if (progressInsights?.length > 0) {
      const latestInsight = progressInsights[0];
      if (latestInsight.type === 'achievement') {
        return `🎉 ${latestInsight.title}`;
      }
      if (latestInsight.type === 'improvement') {
        return `📈 ${latestInsight.title}`;
      }
    }

    return 'Your AI learning companion is ready to help!';
  }, [insights]);

  return (
    <Card style={[styles.container, { backgroundColor: theme.colors.surface }]}>
      <Card.Content style={styles.content}>
        <View style={styles.header}>
          <View style={styles.iconContainer}>
            <ContextualIcon size={24} color={theme.colors.primary} />
            <Sparkles
              size={16}
              color={theme.colors.primary}
              style={styles.sparkleIcon}
            />
          </View>

          {onRefresh && (
            <IconButton
              icon={({ size, color }) =>
                loading ? (
                  <ActivityIndicator size={size} color={color} />
                ) : (
                  <RefreshCw size={size} color={color} />
                )
              }
              size={20}
              onPress={onRefresh}
              disabled={loading}
              style={styles.refreshButton}
            />
          )}
        </View>

        <View style={styles.greetingContainer}>
          <Text
            variant='headlineSmall'
            style={[styles.greeting, { color: theme.colors.onSurface }]}
          >
            {contextualGreeting}
          </Text>

          {aiInsightMessage && (
            <Text
              variant='bodyMedium'
              style={[styles.insight, { color: theme.colors.onSurfaceVariant }]}
            >
              {aiInsightMessage}
            </Text>
          )}
        </View>

        {insights?.streakInfo && (
          <View style={styles.streakContainer}>
            <View style={styles.streakBadge}>
              <Text
                variant='labelSmall'
                style={[styles.streakText, { color: theme.colors.onPrimary }]}
              >
                {insights.streakInfo.current} day streak
              </Text>
            </View>

            {insights.streakInfo.target > insights.streakInfo.current && (
              <Text
                variant='bodySmall'
                style={[
                  styles.streakTarget,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                {insights.streakInfo.target - insights.streakInfo.current} days
                to target
              </Text>
            )}
          </View>
        )}
      </Card.Content>
    </Card>
  );
}

const styles = StyleSheet.create({
  container: {
    margin: 16,
    marginBottom: 8,
  },
  content: {
    paddingVertical: 20,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  iconContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
  },
  sparkleIcon: {
    position: 'absolute',
    top: -8,
    right: -8,
  },
  refreshButton: {
    margin: 0,
  },
  greetingContainer: {
    marginBottom: 16,
  },
  greeting: {
    fontWeight: '600',
    marginBottom: 4,
  },
  insight: {
    lineHeight: 20,
  },
  streakContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  streakBadge: {
    backgroundColor: '#FF6B35', // Orange color for streak
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  streakText: {
    fontWeight: '600',
    color: 'white',
  },
  streakTarget: {
    fontSize: 12,
  },
});
